/**
 * The partner offer endpoints (partner codes build item 5 / C5) — routers/partner_offer_purchase.py.
 *
 *   fetchPartnerPurchaseOffer(platform)  GET  /api/user/partner-offer?platform=
 *   allocateAppleOfferCode(productId)    POST /api/user/partner-offer/apple-code
 *   reportPartnerOfferFallback(reason)   POST /api/user/partner-offer/fallback
 *
 * Through `apiCall` (services/api.ts → fetchWithAuth): the credential, a bounded timeout, and
 * never a request without a credential. None of these throws: a failure is an answer the
 * caller acts on (no offer → the paywall; a failed allocation → the sheet says so).
 */
import { apiCall } from '@/services/api';
import type { PartnerPurchaseOffer } from '@/utils/partnerOfferSheet';

function send(path: string, method: 'GET' | 'POST', body?: unknown): Promise<Response> {
  return apiCall(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** The discount this person can buy with on this phone, or null (any failure). */
export async function fetchPartnerPurchaseOffer(platform: string): Promise<PartnerPurchaseOffer | null> {
  try {
    const res = await send(`/api/user/partner-offer?platform=${encodeURIComponent(platform)}`, 'GET');
    if (!res.ok) return null;
    const data = await res.json();
    return data && typeof data === 'object' ? (data as PartnerPurchaseOffer) : null;
  } catch {
    return null;
  }
}

export type AppleCodeAnswer =
  | { ok: true; redemptionUrl: string; reused: boolean; switched: boolean }
  | { ok: false; code: string; heldProductId?: string | null; message?: string | null };

/** One App Store one-time code for this product at this person's level, inside Apple's
 *  redemption link. A refusal carries the server's code (`out_of_stock`, `no_grant`, …);
 *  `code_held_for_period` also carries the product the person's code is held for, and
 *  `offer_code_limit` the server's words for the person (`message`). A network failure is
 *  `network`. */
export async function allocateAppleOfferCode(productId: string): Promise<AppleCodeAnswer> {
  try {
    const res = await send('/api/user/partner-offer/apple-code', 'POST', { product_id: productId });
    let data: any = null;
    try { data = await res.json(); } catch { /* no body */ }
    if (res.ok && data?.result === 'allocated' && typeof data.redemption_url === 'string') {
      return {
        ok: true, redemptionUrl: data.redemption_url, reused: data.reused === true, switched: data.switched === true,
      };
    }
    const code = data?.detail?.code;
    const held = data?.detail?.held_product_id;
    const message = data?.detail?.message;
    return {
      ok: false,
      code: typeof code === 'string' ? code : `http_${res.status}`,
      heldProductId: typeof held === 'string' ? held : null,
      ...(typeof message === 'string' ? { message } : {}),   // only offer_code_limit carries words
    };
  } catch {
    return { ok: false, code: 'network' };
  }
}

/** Tell the server the app fell back to the paywall (it logs PARTNER_OFFER_FALLBACK).
 *  Fire and forget. */
export async function reportPartnerOfferFallback(reason: string, productId?: string | null): Promise<void> {
  try {
    await send('/api/user/partner-offer/fallback', 'POST', { reason, product_id: productId ?? null });
  } catch {
    /* observability only */
  }
}
